import { CloseOutlineIcon } from '@carboncopyco/icons';
import {
  TourPreviewPage,
  clearTourEditorState,
  hasInitialTourEditorState,
  setInitialTourEditorState,
  useTourSelector,
} from '@carboncopyco/mimesis-sdk';
import { TourStepEnginePreviewMessageType } from '@carboncopyco/types';
import { type IGlobalTab, TabType } from '@carboncopyco/types/desktop';
import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { settings } from '../../../settings';
import { useEditorPageInfo } from '../components/EditorContainer/hooks';
import PresentAndPreviewToolbar from '../components/PresentAndPreviewToolbar';
import useEditorGroupTab from '../hooks/useEditorGroupTab';
import useGetMimesisStepEngineUrl from '../hooks/useGetMimesisStepEngineUrl';
import { useTabManager } from '../services/TabManager';
import { previewFrameBorder } from './constants/previewFrameBorder';

interface ITourPreviewRoute {
  tab: IGlobalTab;
  isPresentation?: boolean;
}

const TourPreviewRoute = ({
  tab,
  isPresentation = false,
}: ITourPreviewRoute) => {
  const mimesisStepEngineRelativeUrl = useGetMimesisStepEngineUrl();
  const tabRootElement = useRef<HTMLDivElement>(null);
  const pageInfo = useEditorPageInfo(tab);
  const { getActiveGlobalTab, globalTabs, closeGlobalTab, closeGroupTabs } =
    useTabManager();
  const globalTab = getActiveGlobalTab();
  const dispatch = useDispatch();
  const { updateEditorTabVisibility, getFirstGroupTabByGroupId } =
    useEditorGroupTab();

  const uuid = String(tab.id);
  const isStateInitialised = useTourSelector(hasInitialTourEditorState(uuid));

  const initFrameDeps = [globalTabs.map(({ id }) => id).join('-')];

  useEffect(() => {
    if (!isStateInitialised) {
      dispatch(setInitialTourEditorState({ uuid }));
    }
  }, [isStateInitialised]);

  useEffect(() => {
    return () => {
      dispatch(clearTourEditorState({ uuid }));
    };
  }, []);

  const onExitPreviewClick = () => {
    if (tab.payload?.isOpenedFromEditor) {
      const editorTab = tab.group && getFirstGroupTabByGroupId(tab.group.id);
      if (editorTab) {
        updateEditorTabVisibility({
          editorTab,
          show: true,
        });
      }
      closeGlobalTab(tab.id);
      return;
    }

    closeGroupTabs(globalTab);
  };

  return (
    <div
      className="flex flex-col flex-grow shrink basis-0 min-h-0 bg-white"
      ref={tabRootElement}
    >
      <div className="flex-shrink-0">
        <PresentAndPreviewToolbar
          tab={tab}
          onGoBack={() =>
            window.postMessage(
              {
                type: TourStepEnginePreviewMessageType.onBackStep,
              },
              '*'
            )
          }
          onGoForward={() =>
            window.postMessage(
              {
                type: TourStepEnginePreviewMessageType.onNextStep,
              },
              '*'
            )
          }
          onRestart={() =>
            window.postMessage(
              {
                type: TourStepEnginePreviewMessageType.onRestartTour,
              },
              '*'
            )
          }
          speakerPannel={{
            isVisible:
              isPresentation || tab.type === TabType.GROUP_TOUR_PREVIEW,
          }}
          pageInfo={pageInfo}
          ctaButton={{
            title: 'Exit preview',
            onClick: onExitPreviewClick,
            icon: <CloseOutlineIcon />,
          }}
        />
      </div>
      <div
        className={classNames('flex flex-col grow shrink min-h-0', {
          [previewFrameBorder]: !isPresentation,
        })}
      >
        <TourPreviewPage
          uuid={uuid}
          mimesisStepEngineUrl={mimesisStepEngineRelativeUrl}
          fileServiceDynamicLink={settings.FILES_SERVICE_URL}
          isDebugMode={false}
          activeTabId={globalTab.id}
          rootElement={tabRootElement}
          tabId={tab?.id}
          tourData={tab?.payload?.tour}
          tourFramesData={tab?.payload?.presentation?.frames}
          initFrameDeps={initFrameDeps}
          showWithoutDrafts={!tab?.payload?.isOpenedFromEditor}
        />
      </div>
    </div>
  );
};

export default TourPreviewRoute;
